import { useMemo } from 'react'
import { columnLeadDays, hasColumnDates } from '@/shared/templates/memos'
import type { MemoCandidateColumn, MemoTemplate } from '@/shared/templates/memos'
import { seedStart } from '../lib/spanStart'
import { seededSpan, useSpanEdits } from './useSeededSpan'
import type { SeededSpan, SpanEdits } from './useSeededSpan'

type ColumnEdits = Partial<Record<MemoCandidateColumn, SpanEdits>>

/**
 * A span of days per candidate column, for a template whose columns are about
 * different days - a Print-PM lists tomorrow's paper beside today's articles.
 * Each opens on the memo's day plus its column's lead, see `columnLeadDays`, and
 * is otherwise seeded and reset the way the memo's own span is.
 *
 * Undefined for a template without days per column, see `hasColumnDates`.
 */
export const useColumnDateRanges = ({
  template,
  templateId,
  memoDate,
  planningId
}: {
  template: MemoTemplate | undefined
  templateId: string
  memoDate: string
  planningId: string | undefined
}): Record<MemoCandidateColumn, SeededSpan> | undefined => {
  const [edits, setEdits] = useSpanEdits<ColumnEdits>({}, templateId, planningId)

  return useMemo(() => {
    if (!template || !hasColumnDates(template)) {
      return undefined
    }

    const spanOf = (column: MemoCandidateColumn) => seededSpan(
      seedStart(memoDate, template, columnLeadDays(template, column)),
      template.defaultSpanDays ?? 0,
      edits[column] ?? {},
      (next) => setEdits((current) => ({ ...current, [column]: next(current[column] ?? {}) }))
    )

    return {
      print: spanOf('print'),
      planning: spanOf('planning'),
      article: spanOf('article'),
      events: spanOf('events')
    }
  }, [template, memoDate, edits, setEdits])
}
